import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../components/ui/select';
import { 
  Rocket, 
  ArrowLeft, 
  ArrowRight,
  CheckCircle,
  Palette,
  Video,
  Globe,
  Smartphone,
  FileText,
  Image,
  Megaphone,
  PenTool,
  Upload,
  Calendar,
  DollarSign,
  Clock,
  Sparkles,
  AlertCircle,
  X,
  Loader2
} from 'lucide-react';
import { cn } from '../../lib/utils';
import api from '../../lib/axios';
import { toast } from 'sonner';

const PROJECT_TYPES = [
  { id: 'logo', name: 'Logo & Identité', icon: Palette, color: 'bg-pink-500', description: 'Logo, charte graphique, papeterie' },
  { id: 'visual', name: 'Création Graphique', icon: Image, color: 'bg-purple-500', description: 'Affiches, flyers, brochures, packaging' },
  { id: 'video', name: 'Vidéo & Motion', icon: Video, color: 'bg-red-500', description: 'Vidéo corporate, motion design, aftermovie' },
  { id: 'website', name: 'Site Web', icon: Globe, color: 'bg-cyan-500', description: 'Site vitrine, e-commerce, landing page' },
  { id: 'mobile_app', name: 'Application Mobile', icon: Smartphone, color: 'bg-blue-500', description: 'App iOS, Android, cross-platform' },
  { id: 'marketing', name: 'Marketing Digital', icon: Megaphone, color: 'bg-orange-500', description: 'Social ads, Google Ads, email marketing' },
  { id: 'social', name: 'Réseaux Sociaux', icon: PenTool, color: 'bg-indigo-500', description: 'Gestion de comptes, création de contenus' },
  { id: 'other', name: 'Autre projet', icon: Sparkles, color: 'bg-slate-500', description: 'Décrivez votre besoin' },
];

const BUDGET_RANGES = [
  { id: 'small', label: 'Moins de 1 000€', min: 0, max: 1000 },
  { id: 'medium', label: '1 000€ - 3 000€', min: 1000, max: 3000 },
  { id: 'large', label: '3 000€ - 5 000€', min: 3000, max: 5000 },
  { id: 'xlarge', label: '5 000€ - 10 000€', min: 5000, max: 10000 },
  { id: 'enterprise', label: 'Plus de 10 000€', min: 10000, max: 50000 },
  { id: 'unknown', label: 'Je ne sais pas encore', min: 0, max: 0 },
];

const URGENCY_OPTIONS = [
  { id: 'asap', label: 'Urgent (< 1 semaine)', days: 7, color: 'text-red-600 bg-red-50 border-red-200' },
  { id: 'normal', label: 'Normal (2-4 semaines)', days: 21, color: 'text-amber-600 bg-amber-50 border-amber-200' },
  { id: 'relaxed', label: 'Flexible (1-2 mois)', days: 45, color: 'text-green-600 bg-green-50 border-green-200' },
  { id: 'planning', label: 'En préparation (> 2 mois)', days: 90, color: 'text-blue-600 bg-blue-50 border-blue-200' },
];

const NewProjectPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    project_type: '',
    name: '',
    description: '',
    budget_range: '',
    urgency: '',
    deadline: '',
    references: '',
    files: [],
    additional_notes: ''
  });

  const steps = [
    { title: 'Type de projet', icon: Palette },
    { title: 'Détails', icon: FileText },
    { title: 'Budget & Délai', icon: Calendar },
    { title: 'Récapitulatif', icon: CheckCircle }
  ];

  const canProceed = () => {
    switch (currentStep) {
      case 0: return !!formData.project_type;
      case 1: return formData.name.trim().length >= 3 && formData.description.trim().length >= 20;
      case 2: return !!formData.budget_range && !!formData.urgency;
      case 3: return true;
      default: return false;
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      // Calculate estimated budget based on range
      const budgetRange = BUDGET_RANGES.find(b => b.id === formData.budget_range);
      const estimatedBudget = budgetRange ? (budgetRange.min + budgetRange.max) / 2 : 0;
      
      // Calculate deadline
      const urgencyOption = URGENCY_OPTIONS.find(u => u.id === formData.urgency);
      const deadline = formData.deadline || new Date(Date.now() + (urgencyOption?.days || 30) * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

      const projectData = {
        name: formData.name,
        project_type: formData.project_type,
        status: 'draft',
        description: formData.description,
        budget: estimatedBudget,
        deadline: deadline,
        metadata: {
          budget_range: formData.budget_range,
          urgency: formData.urgency,
          references: formData.references,
          additional_notes: formData.additional_notes,
          submitted_by_client: true
        }
      };

      const response = await api.post('/projects', projectData);
      toast.success('🎉 Votre demande de projet a été soumise ! Notre équipe vous contactera sous 24h.');
      navigate(`/projects/${response.data.id}`);
    } catch (error) {
      console.error('Error submitting project:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la soumission');
    } finally {
      setSubmitting(false);
    }
  };

  const selectedType = PROJECT_TYPES.find(t => t.id === formData.project_type);

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar 
        collapsed={collapsed} 
        setCollapsed={setCollapsed}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
      />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
      
      <main className={cn(
        "transition-all duration-300 p-6 md:p-8 pt-24",
        collapsed ? "md:ml-20" : "md:ml-64"
      )}>
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate(-1)}
              className="text-slate-600 hover:text-slate-800"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-[#1a365d]">
                Nouveau projet
              </h1>
              <p className="text-[#64748b]">
                Décrivez votre besoin et recevez un devis sous 24h
              </p>
            </div>
          </div>
        </div>

        {/* Steps Indicator */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {steps.map((step, idx) => (
            <React.Fragment key={idx}>
              <button
                onClick={() => idx < currentStep && setCurrentStep(idx)}
                className={cn(
                  "flex items-center gap-2 px-4 py-2 rounded-full transition-all",
                  idx === currentStep
                    ? "bg-gradient-to-r from-cyan-500 to-blue-500 text-white"
                    : idx < currentStep
                      ? "bg-emerald-100 text-emerald-700 cursor-pointer hover:bg-emerald-200"
                      : "bg-gray-100 text-gray-400"
                )}
              >
                {idx < currentStep ? (
                  <CheckCircle className="w-4 h-4" />
                ) : (
                  <step.icon className="w-4 h-4" />
                )}
                <span className="text-sm font-medium hidden sm:inline">{step.title}</span>
              </button>
              {idx < steps.length - 1 && (
                <div className={cn(
                  "w-12 h-1 rounded-full",
                  idx < currentStep ? "bg-emerald-300" : "bg-gray-200"
                )} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Form Content */}
        <Card className="card max-w-3xl mx-auto">
          <CardContent className="p-6 md:p-8">
            
            {/* Step 1: Project Type */}
            {currentStep === 0 && (
              <div className="space-y-6">
                <div className="text-center mb-8">
                  <h2 className="text-xl font-bold text-[#1a365d] mb-2">
                    Quel type de projet souhaitez-vous réaliser ?
                  </h2>
                  <p className="text-[#64748b]">
                    Sélectionnez la catégorie qui correspond le mieux à votre besoin
                  </p>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {PROJECT_TYPES.map((type) => (
                    <button
                      key={type.id}
                      onClick={() => setFormData({ ...formData, project_type: type.id })}
                      className={cn(
                        "p-4 rounded-xl border-2 text-center transition-all hover:shadow-lg group",
                        formData.project_type === type.id
                          ? "border-cyan-500 bg-cyan-50 shadow-md"
                          : "border-slate-200 hover:border-slate-300"
                      )}
                    >
                      <div className={cn(
                        "w-12 h-12 rounded-xl mx-auto mb-3 flex items-center justify-center transition-transform group-hover:scale-110",
                        type.color
                      )}>
                        <type.icon className="w-6 h-6 text-white" />
                      </div>
                      <p className="font-semibold text-sm text-[#1a365d] mb-1">{type.name}</p>
                      <p className="text-xs text-[#64748b] line-clamp-2">{type.description}</p>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Step 2: Details */}
            {currentStep === 1 && (
              <div className="space-y-6">
                <div className="text-center mb-8">
                  <div className={cn("w-16 h-16 rounded-xl mx-auto mb-4 flex items-center justify-center", selectedType?.color)}>
                    {selectedType && <selectedType.icon className="w-8 h-8 text-white" />}
                  </div>
                  <h2 className="text-xl font-bold text-[#1a365d] mb-2">
                    Décrivez votre projet
                  </h2>
                  <p className="text-[#64748b]">
                    Plus vous êtes précis, plus notre devis sera adapté
                  </p>
                </div>

                <div>
                  <Label className="text-[#1a365d]">Nom du projet *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Ex: Refonte logo entreprise, Vidéo corporate, Site e-commerce..."
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label className="text-[#1a365d]">Description détaillée *</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Décrivez votre projet en détail : objectifs, cible, contexte, contraintes..."
                    rows={5}
                    className="mt-2"
                  />
                  <p className="text-xs text-[#64748b] mt-1">
                    {formData.description.length}/20 caractères minimum
                  </p>
                </div>

                <div>
                  <Label className="text-[#1a365d]">Références et inspirations (optionnel)</Label>
                  <Textarea
                    value={formData.references}
                    onChange={(e) => setFormData({ ...formData, references: e.target.value })}
                    placeholder="Partagez des liens ou décrivez des exemples qui vous plaisent..."
                    rows={3}
                    className="mt-2"
                  />
                </div>
              </div>
            )}

            {/* Step 3: Budget & Deadline */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <div className="text-center mb-8">
                  <h2 className="text-xl font-bold text-[#1a365d] mb-2">
                    Budget et délais
                  </h2>
                  <p className="text-[#64748b]">
                    Aidez-nous à vous proposer une offre adaptée
                  </p>
                </div>

                <div>
                  <Label className="text-[#1a365d] flex items-center gap-2 mb-3">
                    <DollarSign className="w-4 h-4" />
                    Budget envisagé *
                  </Label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {BUDGET_RANGES.map((budget) => (
                      <button
                        key={budget.id}
                        onClick={() => setFormData({ ...formData, budget_range: budget.id })}
                        className={cn(
                          "p-3 rounded-xl border-2 text-center transition-all",
                          formData.budget_range === budget.id
                            ? "border-cyan-500 bg-cyan-50"
                            : "border-slate-200 hover:border-slate-300"
                        )}
                      >
                        <span className="text-sm font-medium text-[#1a365d]">{budget.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <Label className="text-[#1a365d] flex items-center gap-2 mb-3">
                    <Clock className="w-4 h-4" />
                    Niveau d'urgence *
                  </Label>
                  <div className="grid grid-cols-2 gap-3">
                    {URGENCY_OPTIONS.map((option) => (
                      <button
                        key={option.id}
                        onClick={() => setFormData({ ...formData, urgency: option.id })}
                        className={cn(
                          "p-4 rounded-xl border-2 text-left transition-all",
                          formData.urgency === option.id
                            ? `border-cyan-500 ${option.color}`
                            : "border-slate-200 hover:border-slate-300"
                        )}
                      >
                        <span className={cn(
                          "text-sm font-medium",
                          formData.urgency === option.id ? "" : "text-[#1a365d]"
                        )}>
                          {option.label}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <Label className="text-[#1a365d] flex items-center gap-2 mb-2">
                    <Calendar className="w-4 h-4" />
                    Date limite souhaitée (optionnel)
                  </Label>
                  <Input
                    type="date"
                    value={formData.deadline}
                    onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
                    min={new Date().toISOString().split('T')[0]}
                    className="max-w-xs"
                  />
                </div>

                <div>
                  <Label className="text-[#1a365d]">Notes supplémentaires</Label>
                  <Textarea
                    value={formData.additional_notes}
                    onChange={(e) => setFormData({ ...formData, additional_notes: e.target.value })}
                    placeholder="Informations complémentaires, contraintes particulières..."
                    rows={2}
                    className="mt-2"
                  />
                </div>
              </div>
            )}

            {/* Step 4: Summary */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <div className="text-center mb-8">
                  <div className="w-16 h-16 rounded-full bg-emerald-100 mx-auto mb-4 flex items-center justify-center">
                    <CheckCircle className="w-8 h-8 text-emerald-600" />
                  </div>
                  <h2 className="text-xl font-bold text-[#1a365d] mb-2">
                    Récapitulatif de votre demande
                  </h2>
                  <p className="text-[#64748b]">
                    Vérifiez les informations avant de soumettre
                  </p>
                </div>

                <div className="space-y-4 bg-slate-50 rounded-xl p-6">
                  <div className="flex items-start gap-4">
                    <div className={cn("w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0", selectedType?.color)}>
                      {selectedType && <selectedType.icon className="w-6 h-6 text-white" />}
                    </div>
                    <div>
                      <p className="text-sm text-[#64748b]">Type de projet</p>
                      <p className="font-semibold text-[#1a365d]">{selectedType?.name}</p>
                    </div>
                  </div>

                  <hr className="border-slate-200" />

                  <div>
                    <p className="text-sm text-[#64748b]">Nom du projet</p>
                    <p className="font-semibold text-[#1a365d]">{formData.name}</p>
                  </div>

                  <div>
                    <p className="text-sm text-[#64748b]">Description</p>
                    <p className="text-[#1a365d] text-sm">{formData.description}</p>
                  </div>

                  {formData.references && (
                    <div>
                      <p className="text-sm text-[#64748b]">Références</p>
                      <p className="text-[#1a365d] text-sm">{formData.references}</p>
                    </div>
                  )}

                  <hr className="border-slate-200" />

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-[#64748b]">Budget</p>
                      <p className="font-semibold text-[#1a365d]">
                        {BUDGET_RANGES.find(b => b.id === formData.budget_range)?.label}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-[#64748b]">Urgence</p>
                      <p className="font-semibold text-[#1a365d]">
                        {URGENCY_OPTIONS.find(u => u.id === formData.urgency)?.label}
                      </p>
                    </div>
                  </div>

                  {formData.deadline && (
                    <div>
                      <p className="text-sm text-[#64748b]">Date limite</p>
                      <p className="font-semibold text-[#1a365d]">
                        {new Date(formData.deadline).toLocaleDateString('fr-FR', { 
                          day: 'numeric', month: 'long', year: 'numeric' 
                        })}
                      </p>
                    </div>
                  )}
                </div>

                <div className="p-4 bg-cyan-50 border border-cyan-200 rounded-xl">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-cyan-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-cyan-800">Prochaines étapes</p>
                      <p className="text-sm text-cyan-700 mt-1">
                        Notre équipe analysera votre demande et vous enverra un devis personnalisé sous 24h. 
                        Vous serez notifié par email et dans votre espace client.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8 pt-6 border-t border-slate-200">
              <Button
                variant="outline"
                onClick={() => currentStep > 0 ? setCurrentStep(currentStep - 1) : navigate(-1)}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                {currentStep === 0 ? 'Annuler' : 'Retour'}
              </Button>

              {currentStep < 3 ? (
                <Button
                  onClick={() => setCurrentStep(currentStep + 1)}
                  disabled={!canProceed()}
                  className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600"
                >
                  Continuer
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Envoi en cours...
                    </>
                  ) : (
                    <>
                      <Rocket className="w-4 h-4 mr-2" />
                      Soumettre ma demande
                    </>
                  )}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default NewProjectPage;
